import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Sparkles, Info, Zap, Timer, HeartHandshake } from "lucide-react";
import { ShowMore } from "./ShowMore";
import { api } from "@/lib/api";

// Item 4 (2026-07): top 3 by default — "Show more" reveals the rest in place.
const INITIAL_CARDS = 3;

// Phase 18 item 1: "Quick wins near you" — a row INSIDE Find your next cause
// (the ≤7 top-level modules rule stands). Top 3 true quick wins (shared
// server-side definition) within the alerts radius, mixing help requests and
// opportunities. Hidden below 2 results. Copy rule: "under an hour", never
// "micro-volunteering".
function QuickWinsRow() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    let cancelled = false;
    api.quickWins().then((rows) => { if (!cancelled) setItems(rows || []); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);
  if (items.length < 2) return null;
  return (
    <div className="mt-4" data-testid="quick-wins-row">
      <div className="text-xs uppercase tracking-[0.18em] text-terracotta font-bold flex items-center gap-1.5">
        <Timer className="w-3.5 h-3.5" /> Quick wins near you
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2">
        {items.map((o) => (
          <Link
            key={o.id}
            to={`/opportunities/${o.id}`}
            data-testid={`quick-win-card-${o.id}`}
            className="group bg-terracotta/5 border border-terracotta/20 rounded-xl p-4 hover:border-terracotta/50 transition-colors block"
          >
            <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.14em] text-terracotta">
              {o.is_help_request ? (
                <><HeartHandshake className="w-3.5 h-3.5" /> Neighbour needs a hand</>
              ) : (
                <><Timer className="w-3.5 h-3.5" /> Under an hour</>
              )}
            </div>
            <div className="font-medium text-forest text-sm mt-1.5 truncate group-hover:text-forest-hover">{o.title}</div>
            <div className="text-xs text-neutral-500 mt-1">
              {o.distance_miles} mi away{o.event_date ? ` · ${o.event_date}` : " · flexible timing"}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function WhyMatchTooltip({ item, children }) {
  const hasReasons = (item.matched_values || []).length > 0 || (item.matched_skills || []).length > 0;
  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent data-testid={`match-tooltip-${item.id}`} className="max-w-xs">
          <div className="space-y-1.5">
            <div className="font-semibold">Why we matched this</div>
            {hasReasons ? (
              <div className="flex flex-wrap gap-1">
                {(item.matched_values || []).map(v => (
                  <span key={`v-${v}`} className="px-1.5 py-0.5 rounded bg-forest/15 text-[11px] capitalize">{v}</span>
                ))}
                {(item.matched_skills || []).map(s => (
                  <span key={`s-${s}`} className="px-1.5 py-0.5 rounded bg-terracotta/15 text-[11px] capitalize">{s}</span>
                ))}
              </div>
            ) : (
              <div className="text-xs opacity-80">{item._src === "rec" ? "AI pick based on your profile." : "Close to your area."}</div>
            )}
            {item.distance_km != null && (
              <div className="text-xs opacity-80">{item.distance_km} km from you</div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function ForYouCard({ item, i, applyToOpp }) {
  const fromMatch = item._src === "matched";
  const cardId = fromMatch ? `matched-card-${item.id}` : `recommendation-card-${item.id}`;
  const applyId = fromMatch ? `matched-apply-${item.id}` : `rec-apply-${item.id}`;
  return (
    <div
      data-testid={cardId}
      className="relative bg-white border border-neutral-200 rounded-xl p-5 hover:-translate-y-0.5 transition-transform animate-fade-up"
      style={{ animationDelay: `${Math.min(i, 8) * 90}ms` }}
    >
      <WhyMatchTooltip item={item}>
        <Link to={`/opportunities/${item.id}`} className="block">
          <div className="text-xs uppercase tracking-[0.18em] text-terracotta/80 font-bold truncate">{item.org_name}</div>
          <div className="font-display text-lg text-forest mt-1">{item.title}</div>
          <div className="text-xs text-neutral-500 mt-1">
            {item.location}{item.distance_km != null ? ` · ${item.distance_km} km` : ""}
          </div>
          <div className="inline-flex items-center gap-1 text-[11px] text-forest/70 mt-3 font-medium">
            <Sparkles className="w-3 h-3" /> Why this match?
          </div>
        </Link>
      </WhyMatchTooltip>
      {item.is_help_request ? (
        // Neighbour help deserves a review step (user decision, 2026-07) —
        // no instant apply from recommendation cards; go read the request.
        <Button asChild data-testid={`rec-view-request-${item.id}`} size="sm" className="mt-4 rounded-full bg-sky-600 hover:bg-sky-700 h-8 px-4 text-xs">
          <Link to={`/opportunities/${item.id}`}>View request</Link>
        </Button>
      ) : (
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button data-testid={applyId} size="sm" className="mt-4 rounded-full bg-forest hover:bg-forest-hover h-8 px-4 text-xs">
            Apply
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent data-testid={`matched-apply-confirm-${item.id}`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Apply to {item.title}?</AlertDialogTitle>
            <AlertDialogDescription>
              We'll let {item.org_name} know you're interested in volunteering.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`matched-apply-cancel-${item.id}`}>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid={`matched-apply-yes-${item.id}`} onClick={() => applyToOpp(item.id)} className="bg-forest hover:bg-forest-hover">
              Yes, apply
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      )}
    </div>
  );
}

/**
 * Phase 6 / Item 4 (2026-07): "Find your next cause" — ONE module merging the
 * old swipe-deck CTA card and "Recommended for you". Top 3 recommendation
 * cards with Apply buttons, a "Swipe through all matches" button into
 * /discover, and Show more revealing the rest in place. Matched items come
 * first (they carry distance + why-match chips); AI picks fill in behind,
 * deduped by opportunity id.
 */
export function ForYouSection({ recs, inspiration, matched, applyToOpp }) {
  const [limit, setLimit] = useState(INITIAL_CARDS);

  const merged = useMemo(() => {
    const seen = new Set(matched.map(m => m.id));
    return [
      ...matched.map(m => ({ ...m, _src: "matched" })),
      ...(recs.items || []).filter(o => !seen.has(o.id)).map(o => ({ ...o, _src: "rec" })),
    ];
  }, [matched, recs.items]);

  return (
    <div className="mt-10" data-testid="recommendations-section">
      <div className="flex items-center gap-2 flex-wrap">
        <h2 className="font-display text-2xl text-forest tracking-tight">Find your next cause</h2>
        <TooltipProvider delayDuration={100}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span data-testid="recommendations-info" className="text-neutral-400 cursor-help">
                <Info className="w-4 h-4" />
              </span>
            </TooltipTrigger>
            <TooltipContent side="right" className="max-w-xs">
              AI-powered picks blended with matches on your values, skills and distance. Apply instantly from any card.
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <Button asChild data-testid="discover-entry-btn" size="sm" className="ml-auto rounded-full bg-forest hover:bg-forest-hover h-9 px-5 text-xs">
          <Link to="/discover"><Zap className="w-3.5 h-3.5 mr-1.5" /> Swipe through all matches</Link>
        </Button>
      </div>
      <div className="mt-4" data-testid="recommendations-body">
        <QuickWinsRow />
        {recs.reason === "profile_incomplete" ? (
          <div data-testid="empty-profile-nudge">
            <div className="bg-white border border-dashed border-forest/25 rounded-xl p-6 flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-terracotta/10 text-terracotta flex items-center justify-center shrink-0">
                <Sparkles className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-forest">Complete your profile to get personalised recommendations</div>
                <p className="text-sm text-neutral-600 mt-1 max-w-lg">
                  Add a short bio, a few values and skills — our AI matcher will use them to surface opportunities you'll love.
                </p>
                <Link data-testid="recommendations-complete-profile" to="/onboarding" className="mt-3 inline-flex items-center gap-1 text-sm text-terracotta hover:text-terracotta-hover font-medium">
                  Complete profile →
                </Link>
              </div>
            </div>
            {inspiration.length > 0 && (
              <div className="mt-4">
                <div className="text-xs uppercase tracking-[0.18em] text-forest/60 font-bold mb-2">For inspiration — what your neighbours are doing</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {inspiration.map((o) => (
                    <Link
                      key={o.id}
                      to={`/opportunities/${o.id}`}
                      data-testid={`inspiration-card-${o.id}`}
                      className="group bg-white border border-neutral-200 rounded-2xl p-5 hover:border-forest/40 hover:shadow-sm transition-all block"
                    >
                      <div className="text-xs uppercase tracking-[0.16em] text-forest/60 font-bold">{o.org_name}</div>
                      <div className="font-display text-base text-forest mt-1 truncate group-hover:text-forest-hover">{o.title}</div>
                      <p className="text-sm text-neutral-600 mt-2 line-clamp-2">{o.description}</p>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : merged.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="foryou-row">
              {merged.slice(0, limit).map((item, i) => (
                <ForYouCard key={item.id} item={item} i={i} applyToOpp={applyToOpp} />
              ))}
            </div>
            <ShowMore
              shown={Math.min(limit, merged.length)}
              total={merged.length}
              onMore={() => setLimit(l => l + INITIAL_CARDS)}
              testId="foryou-show-more"
            />
          </>
        ) : (
          <div
            data-testid="recommendations-empty"
            className="bg-white border border-dashed border-forest/25 rounded-xl p-6 text-sm text-neutral-500"
          >
            No matches yet — pick more values & skills in your profile, or check back soon.
          </div>
        )}
      </div>
    </div>
  );
}
